import FeatureSettingsApi from '@/api/featureSettings'
import Sentry from '@/services/sentry'
import { trackFeatureSettings } from '@/business-logic/ai-tracking'

const SET_FEATURE_SETTINGS = 'SET_FEATURE_SETTINGS'

const _state = {
  featureSettings: {},
  isFeatureSettingsLoaded: false,
}

const _getters = {
  isFeatureDisabled: (state) => (featureName) => {
    const feature = state.featureSettings[featureName]
    return feature ? feature.isDisabled : false
  },
  featureDisabledReason: (state) => (featureName) => {
    const feature = state.featureSettings[featureName]
    return feature ? feature.disabledReason : null
  },
  isFeatureSettingsLoaded: (state) => state.isFeatureSettingsLoaded,
}

let prefetchedFeatureSettingsRequest = null
// Bumped by every fetch but the first load's, so a late refresh of a shared copy never lands over its result.
let fetchGeneration = 0

const reportFetchFailure = (e) => {
  // A 401 here means the cached isAuth context outlived the presenter session — expected, not a bug.
  if (e?.status !== 401) {
    Sentry.captureException(e)
  }
  trackFeatureSettings({ stage: 'failed', error: e })
}

// AHA-48350: an add-in slide frame may first resolve the settings another frame stored; this page's
// own fetch then replaces them, or, if it fails, leaves them in place.
// The flags' context carries the user id before the current user is committed, which is when the prefetch runs.
const sharedCopyOptions = (dispatch, rootState) => {
  const generation = fetchGeneration
  return {
    userId: rootState?.currentUser?.id || rootState?.featureToggles?.context?.userId,
    onSharedCopyServed: (_, refresh) => refresh.then(
      (featureSettings) => generation === fetchGeneration &&
        dispatch('applyRefreshedFeatureSettings', featureSettings),
      reportFetchFailure,
    ),
  }
}

const actions = {
  // Not committed here: the next fetchFeatureSettings({ reusePrefetch: true }) commits the result.
  prefetchFeatureSettings({ dispatch, rootState }) {
    prefetchedFeatureSettingsRequest = FeatureSettingsApi.getFeatureSettings(sharedCopyOptions(dispatch, rootState))
    // Marks the rejection handled until fetchFeatureSettings awaits it, which still sees the error.
    prefetchedFeatureSettingsRequest.catch(() => {})
  },
  // A pending prefetch is replaced rather than raced, so the stale copy cannot land after this value
  // and the loaded flag still waits for the prefetch's own commit.
  applyRefreshedFeatureSettings({ commit }, featureSettings) {
    if (prefetchedFeatureSettingsRequest) {
      prefetchedFeatureSettingsRequest = Promise.resolve(featureSettings)
      return
    }
    commit(SET_FEATURE_SETTINGS, featureSettings)
  },
  async fetchFeatureSettings({ commit, dispatch, rootState }, options) {
    // Any fetch retires the prefetch: one started before a settings save must never be committed after it.
    const prefetchedRequest = options?.reusePrefetch ? prefetchedFeatureSettingsRequest : null
    prefetchedFeatureSettingsRequest = null
    if (!options?.reusePrefetch) fetchGeneration += 1
    try {
      trackFeatureSettings({ stage: 'started' })
      const featureSettings = await (prefetchedRequest ||
        FeatureSettingsApi.getFeatureSettings(sharedCopyOptions(dispatch, rootState)))
      commit(SET_FEATURE_SETTINGS, featureSettings)
    } catch (e) {
      reportFetchFailure(e)
      // Fail-open: mark as loaded with empty settings (all features enabled)
      commit(SET_FEATURE_SETTINGS, {})
    }
  },
}

const mutations = {
  // eslint-disable-next-line no-param-reassign
  [SET_FEATURE_SETTINGS](state, featureSettings) {
    state.featureSettings = featureSettings // eslint-disable-line no-param-reassign
    state.isFeatureSettingsLoaded = true // eslint-disable-line no-param-reassign
  },
}

export default {
  namespaced: true,
  state: _state,
  getters: _getters,
  actions,
  mutations,
}
